import { get, isArray, set } from 'lodash';

const DEFAULT_ID_SEPARATOR = '_';

const DEFAULT_ID_PREFIX = 'root';

/**
 * Prunes the formData to only include the value for the given tablePath
 * Does not mutate the original formData.
 * Adds the prefix to the pruned formData.
 * If a row is selected from option, it will only include the data for that row.
 *
 * @param formData - The form data object, for the entire form.
 * @param tablePath - The table path in the rjsf id format.
 * @param options - Optional parameters to customize idSeparator and idPrefix.
 * @returns A new object containing only the pruned formData, prepended by the prefix
 */
export const processTableFormData = (
  formData: Record<string, any>,
  tablePath: string,
  options?: {
    idSeparator?: string;
    idPrefix?: string;
    selectRowIndex?: number;
  },
) => {
  const idSeparator = options?.idSeparator ?? DEFAULT_ID_SEPARATOR;
  const idPrefix = options?.idPrefix ?? DEFAULT_ID_PREFIX;
  // Normalize the tablePath to remove the prefix and be in dot notation
  const normalizedPath = tablePath.split(idSeparator).slice(1).join('.');
  let value = get(formData, normalizedPath);

  // filters out other rows if selectRowIndex is specified
  if (isArray(value) && options?.selectRowIndex !== undefined) {
    value = value.filter((_, i) => options.selectRowIndex === i);
  }

  const prunedFormContext = set({}, normalizedPath, value);

  return { [idPrefix]: prunedFormContext };
};
/**
 * Given a tablePath and a fieldPath, mateches the paths to find the row index of the field in the table.
 */
export const findTableRowIndexOfField = (
  tablePath: string,
  fieldPath: string,
  options?: { idSeparator?: string },
) => {
  const idSeparator = options?.idSeparator ?? DEFAULT_ID_SEPARATOR;
  const baseParts = tablePath.split(idSeparator);
  const fullParts = fieldPath.split(idSeparator);

  if (!baseParts.every((part, i) => fullParts[i] === part)) {
    throw new Error('The field path does not match the table path');
  }

  const index = fullParts[baseParts.length];

  const rowIndex = parseInt(index, 10);
  if (isNaN(rowIndex)) {
    throw new Error(`Invalid row index in field path: ${fieldPath}`);
  }
  return rowIndex;
};
